import { useCallback, useEffect, useState } from 'react';
import { BreakDetailDrawer } from '@/features/manager-breaks-demo/components/BreakDetailDrawer';
import { getEngineeringBreakById } from '@/features/manager-breaks-demo/data/breaks-demo-dataset';
import type { EngineeringBreak } from '@/features/manager-breaks-demo/types/breaks';
import { MANAGER_PATTERNS } from '../data/patterns-demo-dataset';
import { PatternStage } from './PatternStage';
import { PatternPager } from './PatternPager';

/** Circular carousel arrow. Faded + inert at the ends of the run. */
function CarouselArrow({
  direction,
  disabled,
  onClick,
}: {
  direction: 'prev' | 'next';
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={direction === 'prev' ? 'Previous pattern' : 'Next pattern'}
      className={`absolute top-1/2 z-20 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border bg-white/80 backdrop-blur transition-all md:flex ${
        direction === 'prev'
          ? 'start-0 md:-start-2 lg:-start-5'
          : 'end-0 md:-end-2 lg:-end-5'
      } ${
        disabled
          ? 'cursor-not-allowed border-zinc-100 text-zinc-200'
          : 'border-zinc-200 text-[#00AEEF] shadow-md hover:scale-105 hover:border-[#00AEEF]/40 active:scale-95'
      }`}
    >
      <svg
        viewBox="0 0 24 24"
        className={`h-5 w-5 ${direction === 'prev' ? 'rotate-180' : ''}`}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M5 12h14M13 6l6 6-6 6" />
      </svg>
    </button>
  );
}

export function NowTab() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedBreakId, setSelectedBreakId] = useState<string | null>(null);
  const lastIndex = MANAGER_PATTERNS.length - 1;

  const selectedBreak: EngineeringBreak | null =
    (selectedBreakId && getEngineeringBreakById(selectedBreakId)) || null;

  const goTo = useCallback(
    (index: number) => {
      setActiveIndex(Math.max(0, Math.min(lastIndex, index)));
    },
    [lastIndex]
  );

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') goTo(activeIndex + 1);
      if (event.key === 'ArrowLeft') goTo(activeIndex - 1);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [activeIndex, goTo]);

  return (
    <section className="mx-auto w-full max-w-[1180px] px-5 pb-16 pt-10 md:px-8">
      {/* Urgency + pager label */}
      <div className="mb-6 flex items-center justify-center gap-4">
        <span className="inline-flex items-center gap-2 rounded-full bg-rose-50 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-rose-600 ring-1 ring-inset ring-rose-200">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-rose-500" />
          </span>
          Needs a decision today
        </span>
        <span className="text-[11px] font-medium uppercase tracking-[0.16em] text-zinc-500">
          Pattern {activeIndex + 1} of {MANAGER_PATTERNS.length}
        </span>
      </div>

      <h1 className="text-center text-4xl font-bold tracking-tight text-black sm:text-5xl">
        What&apos;s breaking right now.
      </h1>
      <p className="mt-3 text-center text-base text-zinc-600">
        Recurring playbook violations, most urgent first.
      </p>

      {/* Carousel */}
      <div className="relative mt-10">
        <CarouselArrow
          direction="prev"
          disabled={activeIndex === 0}
          onClick={() => goTo(activeIndex - 1)}
        />
        <CarouselArrow
          direction="next"
          disabled={activeIndex === lastIndex}
          onClick={() => goTo(activeIndex + 1)}
        />

        <div className="overflow-hidden">
          <div
            className="flex transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]"
            style={{ transform: `translateX(-${activeIndex * 100}%)` }}
          >
            {MANAGER_PATTERNS.map((pattern, patternIndex) => (
              <div
                key={pattern.id}
                className={`w-full shrink-0 px-0.5 transition-opacity duration-500 sm:px-1 ${
                  patternIndex === activeIndex ? 'opacity-100' : 'opacity-40'
                }`}
                aria-hidden={patternIndex !== activeIndex}
              >
                <PatternStage
                  pattern={pattern}
                  onDeepDive={setSelectedBreakId}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <PatternPager
        patterns={MANAGER_PATTERNS}
        activeIndex={activeIndex}
        onSelect={goTo}
      />

      <BreakDetailDrawer
        engineeringBreak={selectedBreak}
        onClose={() => setSelectedBreakId(null)}
      />
    </section>
  );
}
